import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { BarLoader } from "react-spinners";
import { toast } from "react-toastify";

const EditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    form_name: "",
    expires_at: new Date(),
    summary: ""
  });
  const [charCount, setCharCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/form/${id}`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch form");
        }

        const data = await response.json();
        setForm({ 
          ...data, 
          expires_at: new Date(data.expires_at),
          summary: data.summary || ""
        });
        setCharCount(data.summary?.length || 0);
      } catch (error) {
        if (error.status === 401) {
          navigate("/login");
          return;
        }
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSummaryChange = (e) => {
    const input = e.target.value;
    if (input.length <= 250) {
      setForm({ ...form, summary: input });
      setCharCount(input.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
  
    try {
      const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/staff/me`, {
        credentials: 'include'
      });

      if (!meResponse.ok) {
        toast.error("Admin access required");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/form/edit/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          expires_at: form.expires_at.toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update form");
      }
      
      toast.success('Form updated successfully!')
      setTimeout(() => {
        navigate('/admin/forms');
      }, 100);

    } catch (error) {
      if (error.status === 401) {
        navigate("/login");
        return;
      }
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-transparent">
        <BarLoader color="#4F46E5" size={50} />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Edit Form</h1>
        <button
          onClick={() => navigate("/admin/forms")}
          className="bg-blue-300 text-white px-4 py-2 rounded hover:bg-gray-700"
        >
          Back to Forms
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Form Name
            </label>
            <input
              type="text"
              name="form_name"
              value={form.form_name}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Expiration Date
            </label>
            <DatePicker
              selected={form.expires_at}
              onChange={(date) => setForm({ ...form, expires_at: date })}
              showTimeSelect
              dateFormat="Pp"
              minDate={new Date()}
              className="w-full p-2 border rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Summary <span className="text-gray-500 text-xs">({charCount}/250 characters)</span>
            </label>
            <textarea
              name="summary"
              className="w-full p-2 border rounded-md"
              value={form.summary}
              onChange={handleSummaryChange}
              maxLength={250}
              rows={4}
              required
            />
            <div className="text-right text-xs text-gray-500">
              {charCount}/250 characters
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="w-40 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update Form'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditForm;